import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, merge } from 'lodash';

export default {
  props: {
    postServiceAction: {
      type: Function,
      default: undefined,
    },
    patchServiceAction: {
      type: Function,
      default: undefined,
    },
    templateServiceAction: {
      type: Function,
      default: undefined,
    },
    getRecordServiceAction: {
      type: Function,
      required: false,
    },
    validateServiceAction: {
      type: Function,
      default: undefined,
      required: false,
    },
    customAction: {
      type: String,
      required: false,
    },
    //form component to be loaded
    form: {
      type: Object,
      required: true,
      default: undefined,
    },
    // Initial data for the form component. Preferred to use a template service action
    initialData: {
      type: Object,
      default: () => ({}),
    },
    formProps: {
      type: Object,
      required: false,
    },
    // Custom translation parent key to know  where we want to look for our translations
    customResource: {
      type: String,
      default: undefined,
    },
    customRecordId: {
      type: [Number, String],
      required: false,
    }
  },
  data() {
    return {
      allInitialDataLocal: undefined,
      loadComponent: false,
      object: undefined,
      meta: undefined,
      routeParamIdLocal: undefined,
      showDataLocal: undefined,
      validationErrors: undefined,
    };
  },
  watch: {
    initialData: {
      handler() {
        this.allInitialDataLocal = cloneDeep(merge(this.allInitialDataLocal, this.initialData));
      }
    },
  },
  computed: {
    ...mapGetters('app', [
      'isValidationRequired',
      'activeUploads',
    ]),
    allInitialData: {
      get() {
        return this.allInitialDataLocal;
      },
      set(v) {
        this.allInitialDataLocal = v;
        this.$emit('input', v);
      }
    },
    action() {
      return this.customAction || this.$route.meta?.action || undefined;
    },
    recordId() {
      return this.customRecordId || this.$route.params[this.routeParamIdLocal] || this.$route.params.id;
    },
  },
  created() {
    this.initialise();
  },
  methods: {
    ...mapActions('message', [
      'setMessage',
    ]),
    ...mapActions('app', [
      'setPassedValidation',
      'setIsValidationRequired',
      'setValidationErrors',
      'setIsReadyToCheckValidation',
      'setAttachmentTokens',
    ]),
    async initialise() {
      const loading = await this.$store.dispatch('load');
      await this.setSettings();
      await this.loadInitialData();
      this.loadComponent = true;
      this.$store.dispatch('unload', loading);
    },
    setSettings() {
      this.setIsValidationRequired(this.validateServiceAction);
    },
    async loadInitialData() {
      this.allInitialData = { ...this.$route.query }; //use query params, otherwise empty object
      if (this.action === 'new')
        await this.loadNewInitialData();
      else if (this.action === 'edit')
        await this.loadEditInitialData();

      if (this.action === 'new' || this.action === 'edit')
        this.$emit('initialData:loaded', this.allInitialData);
      this.$emit('showData:initialized', this.showDataLocal);
    },
    async loadNewInitialData() {
      if (this.templateServiceAction) {
        const dbInitialData = await this.initialFormData(this.templateServiceAction);
        // Merge priority, starts from last to first from inside merge function arguments
        this.allInitialData = cloneDeep(merge(
          this.initialData,
          dbInitialData,
          this.allInitialData
        ));
      } else {
        this.allInitialData = cloneDeep(merge(
          this.initialData,
          this.allInitialData
        ));
      }
    },
    async loadEditInitialData() {
      try {
        // make two API calls at the same time
        //NOTE: local function to get data (not meta) from api response
        const [initialData, showData] = await Promise.all([
          this.initialFormData(this.getRecordServiceAction.bind(this, this.recordId)),
          this.initialFormData(this.getRecordServiceAction.bind(this, this.recordId), false),
        ]);

        this.allInitialData = initialData;
        this.showDataLocal = showData;
      } catch (error) {
        this.setMessage({
          type: "error",
          message: error,
        });
      }
    },
    async initialFormData(action, only_permitted = true) {
      try {
        let boundAction;
        boundAction = action.bind(this, { only_permitted: only_permitted });

        let { data } = await boundAction();
        delete data.type; // delete type so it doesn't end up in formData
        return data;
      } catch (e) {
        console.log(e);
      }
    },
    async executeServiceAction(payload) {
      this.object = undefined;
      if (this.action == 'new') {
        if (this.postServiceAction) {
          await this.handleSubmit(payload);
        }
      } else if (this.action == "edit") {
        if (this.patchServiceAction) {
          await this.handlePatch(payload);
        }
      }
    },
    async handleValidate(formData) {
      this.setIsReadyToCheckValidation(false);
      if (!this.isValidationRequired) {
        this.setPassedValidation(true);
        return;
      }
      try {
        var loading = await this.$store.dispatch('load');
        const data = await this.validateServiceAction(
          {
            resourceId: this.recordId,
            data: formData
          }
        );
        this.setValidationErrors(data.errors);
      } catch (e) {
        this.setMessage({
          type: "error",
          message: e,
        });
      } finally {
        this.$store.dispatch('unload', loading);
      }
    },
    async handleSubmit(formData) {
      try {
        var loading = await this.$store.dispatch('load');
        const { data, meta } = await this.postServiceAction(
          formData
        );
        await this.uploadAttachments(data, meta);
        this.object = data;
        this.meta = meta;
        this.$emit('post', data);
      } catch (e) {
        this.setMessage({
          type: "error",
          message: e,
        });
      } finally {
        this.$store.dispatch('unload', loading);
      }
    },
    async handlePatch(payload) {
      try {
        var loading = await this.$store.dispatch('load');
        const { data, meta } = await this.patchServiceAction(
          this.recordId,
          payload
        );
        await this.uploadAttachments(data, meta);
        this.object = data;
        this.$emit('patch', data);
      } catch (e) {
        this.setMessage({
          type: "error",
          message: e,
        });
      } finally {
        this.$store.dispatch('unload', loading);
      }
    },
    async uploadAttachments(data, meta) {
      await this.setAttachmentTokens(
        {
          resourceId: data?.id,
          tokens: meta?.attachmentTokens
        }
      );
    },
  },
};